/**
 * The legacy preset library, ported onto the rule engine.
 *
 * The old alert context shipped 43 preset rules in six categories. Porting them turned up which of
 * them ever actually worked: its evaluator switched on a rule `type`, and any type without a case
 * fell through to a branch that read the rule's `field` off a data row. Presets whose "field" was
 * analysis output rather than a column — underlying sentiment, delta extremes, net flow — therefore
 * matched nothing and never fired. Those are restored here as conditions on the market-signals
 * source, which computes exactly those values (see utils/alertMarketSignals).
 *
 * Five presets are deliberately not carried over: new/updated trading calls and the three
 * call-tracker outcomes are events from the trading-call system rather than anything derivable from
 * the scanner dataset, so a rule over this data could not honestly express them.
 */

import { AlertOp, AlertRule, DEFAULT_ACTIONS, emptyGroup, newId } from './alertRules';
import { ELITE_PULSE_SOURCE, MARKET_SIGNALS_SOURCE, STABLE_FETCH_SOURCE } from './alertSources';

export interface AlertPreset {
  /** Legacy rule id, kept so a ported preset is traceable to its origin. */
  legacyId: string;
  name: string;
  sourceId: string;
  field: string;
  op: AlertOp;
  value: string;
  /** Shown in the picker when the behaviour differs from the legacy rule. */
  note?: string;
}

export interface AlertPresetCategory {
  key: string;
  name: string;
  description: string;
  presets: AlertPreset[];
}

const STABLE = STABLE_FETCH_SOURCE.id;
const MARKET = MARKET_SIGNALS_SOURCE.id;

export const ALERT_PRESET_CATEGORIES: AlertPresetCategory[] = [
  {
    key: 'MARKET_OVERVIEW',
    name: 'Market Overview',
    description: 'SPX summary and today-expiry sentiment',
    presets: [
      { legacyId: 'market_summary_bullish_change', name: 'SPX summary turned bullish', sourceId: MARKET, field: 'market_summary_change', op: 'eq', value: 'bullish' },
      { legacyId: 'market_summary_bearish_change', name: 'SPX summary turned bearish', sourceId: MARKET, field: 'market_summary_change', op: 'eq', value: 'bearish' },
      { legacyId: 'spx_today_expiry_sentiment_change', name: 'SPX today-expiry sentiment set', sourceId: MARKET, field: 'spx_today_sentiment', op: 'not_empty', value: '', note: 'Fires when the reading changes, not on every pass.' },
    ],
  },
  {
    key: 'VOLUME_ACTIVITY',
    name: 'Volume & Activity',
    description: 'Most active options and volume peaks',
    presets: [
      { legacyId: 'most_active_option_alert', name: 'Most active option changed', sourceId: MARKET, field: 'most_active_option', op: 'not_empty', value: '' },
      { legacyId: 'most_active_bought_alert', name: 'Most active is being bought', sourceId: MARKET, field: 'most_active_direction', op: 'eq', value: 'bought' },
      { legacyId: 'most_active_sold_alert', name: 'Most active is being sold', sourceId: MARKET, field: 'most_active_direction', op: 'eq', value: 'sold' },
      { legacyId: 'volume_peak_alert', name: 'Price is at a volume peak', sourceId: MARKET, field: 'volume_peak', op: 'not_empty', value: '' },
      { legacyId: 'high_volume_legacy', name: 'Contract volume above 1,000', sourceId: STABLE, field: 'Volume', op: 'gt', value: '1000' },
    ],
  },
  {
    key: 'SENTIMENT_ANALYSIS',
    name: 'Sentiment Analysis',
    description: 'Sentiment areas and per-underlying sentiment',
    presets: [
      { legacyId: 'bearish_sentiment_area', name: 'Price at a bearish sentiment area', sourceId: MARKET, field: 'sentiment_area', op: 'eq', value: 'bearish' },
      { legacyId: 'bullish_sentiment_area', name: 'Price at a bullish sentiment area', sourceId: MARKET, field: 'sentiment_area', op: 'eq', value: 'bullish' },
      { legacyId: 'strong_bullish_sentiment_legacy', name: 'Contract sentiment is bullish', sourceId: STABLE, field: 'Sentiment', op: 'contains', value: 'Bullish' },
      { legacyId: 'strong_bearish_sentiment_legacy', name: 'Contract sentiment is bearish', sourceId: STABLE, field: 'Sentiment', op: 'contains', value: 'Bearish' },
      { legacyId: 'underlying_bullish_legacy', name: 'An underlying turned bullish', sourceId: MARKET, field: 'underlying_sentiment', op: 'contains', value: 'Bullish', note: 'Never fired in the legacy system; the signal is computed now.' },
      { legacyId: 'underlying_bearish_legacy', name: 'An underlying turned bearish', sourceId: MARKET, field: 'underlying_sentiment', op: 'contains', value: 'Bearish', note: 'Never fired in the legacy system; the signal is computed now.' },
      { legacyId: 'underlying_support_legacy', name: 'An underlying is at support', sourceId: MARKET, field: 'underlying_sentiment', op: 'contains', value: 'at Support', note: 'Never fired in the legacy system; the signal is computed now.' },
      { legacyId: 'underlying_resistance_legacy', name: 'An underlying is at resistance', sourceId: MARKET, field: 'underlying_sentiment', op: 'contains', value: 'at Resistance', note: 'Never fired in the legacy system; the signal is computed now.' },
    ],
  },
  {
    key: 'GREEKS_OPTIONS',
    name: 'Greeks & Options',
    description: 'Delta extremes and spot-gamma peaks',
    presets: [
      { legacyId: 'high_delta_legacy', name: 'Delta above 0.7', sourceId: STABLE, field: 'Delta', op: 'gt', value: '0.7' },
      { legacyId: 'delta_max_alert', name: 'Highest delta on the board', sourceId: MARKET, field: 'delta_max_symbol', op: 'not_empty', value: '', note: 'Fires when the leading contract changes.' },
      { legacyId: 'delta_min_alert', name: 'Lowest delta on the board', sourceId: MARKET, field: 'delta_min_symbol', op: 'not_empty', value: '', note: 'Fires when the leading contract changes.' },
      { legacyId: 'spot_gamma_max_alert', name: 'Spot gamma peak above zero', sourceId: MARKET, field: 'spot_gamma_max', op: 'gt', value: '0' },
    ],
  },
  {
    key: 'PREMIUM_FLOW',
    name: 'Premium & Flow',
    description: 'Premium extremes and delta-premium flow',
    presets: [
      { legacyId: 'max_premium_alert', name: 'Largest premium on the board', sourceId: MARKET, field: 'premium_max_symbol', op: 'not_empty', value: '', note: 'Fires when the leading contract changes.' },
      { legacyId: 'min_premium_alert', name: 'Smallest premium on the board', sourceId: MARKET, field: 'premium_min_symbol', op: 'not_empty', value: '', note: 'Fires when the leading contract changes.' },
      { legacyId: 'delta_premium_high_alert', name: 'Delta premium magnitude above 100K', sourceId: STABLE, field: 'Delta Premium', op: 'abs_gte', value: '100000', note: 'Compares magnitude, as the legacy rule did.' },
      { legacyId: 'delta_acc_premium_high_alert', name: 'Delta accumulated premium above 100K', sourceId: STABLE, field: 'Delta Accumulated Premium', op: 'abs_gte', value: '100000', note: 'Compares magnitude, as the legacy rule did.' },
      { legacyId: 'delta_ask_bid_high_alert', name: 'Delta (ask − bid) above 1,000', sourceId: STABLE, field: 'Delta (Ask-Bid)', op: 'gt', value: '1000' },
      { legacyId: 'delta_ask_bid_low_alert', name: 'Delta (ask − bid) below −1,000', sourceId: STABLE, field: 'Delta (Ask-Bid)', op: 'lt', value: '-1000' },
      { legacyId: 'delta_acc_high_alert', name: 'Delta accumulated above 1,000', sourceId: STABLE, field: 'Delta accumelated', op: 'gt', value: '1000' },
      { legacyId: 'delta_acc_low_alert', name: 'Delta accumulated below −1,000', sourceId: STABLE, field: 'Delta accumelated', op: 'lt', value: '-1000' },
    ],
  },
  {
    key: 'CUSTOM_SCORES',
    name: 'Custom Scores',
    description: 'Haltz, Band and Zahn thresholds',
    presets: [
      { legacyId: 'high_haltz_legacy', name: 'Haltz above 0.8', sourceId: STABLE, field: 'Haltz', op: 'gt', value: '0.8' },
      { legacyId: 'high_band_legacy', name: 'Band above 0.8', sourceId: STABLE, field: 'Band', op: 'gt', value: '0.8' },
      { legacyId: 'high_zahn_legacy', name: 'Zahn above 0.8', sourceId: STABLE, field: 'Zahn', op: 'gt', value: '0.8' },
    ],
  },
  {
    key: 'ELITE_PULSE',
    name: 'Elite Pulse',
    description: 'Opportunity shortcuts for the pulse board',
    presets: [
      { legacyId: 'elite_pulse_large_premium', name: 'Opportunity above $1M premium', sourceId: ELITE_PULSE_SOURCE.id, field: 'prem', op: 'gte', value: '1000000' },
      { legacyId: 'elite_pulse_high_relvol', name: 'Relative volume above 10×', sourceId: ELITE_PULSE_SOURCE.id, field: 'rv', op: 'gte', value: '10' },
      { legacyId: 'elite_pulse_short_dated', name: 'Bullish and expiring within a week', sourceId: ELITE_PULSE_SOURCE.id, field: 'dte', op: 'lte', value: '7' },
    ],
  },
];

/** Legacy presets with no honest equivalent here, surfaced so the gap is visible, not silent. */
export const UNPORTED_LEGACY_PRESETS: { legacyId: string; name: string; reason: string }[] = [
  { legacyId: 'new_trading_call_alert', name: 'New trading call', reason: 'an event from the trading-call system, not a property of scanner data' },
  { legacyId: 'trading_call_update_alert', name: 'Trading call updated', reason: 'an event from the trading-call system, not a property of scanner data' },
  { legacyId: 'call_closed_profit_alert', name: 'Call closed in profit', reason: 'a call-tracker outcome, not derivable from the option chain' },
  { legacyId: 'call_closed_loss_alert', name: 'Call closed at a loss', reason: 'a call-tracker outcome, not derivable from the option chain' },
  { legacyId: 'call_did_not_work_alert', name: 'Call did not work out', reason: 'a call-tracker outcome, not derivable from the option chain' },
];

export function presetsForSource(sourceId: string): AlertPresetCategory[] {
  return ALERT_PRESET_CATEGORIES.map((c) => ({
    ...c,
    presets: c.presets.filter((p) => p.sourceId === sourceId),
  })).filter((c) => c.presets.length > 0);
}

/** Turn a preset into a real rule the user can then edit like any other. */
export function ruleFromPreset(preset: AlertPreset): AlertRule {
  const root = emptyGroup('AND');
  root.children.push({
    kind: 'condition',
    id: newId('c'),
    field: preset.field,
    op: preset.op,
    value: preset.value,
  });
  return {
    id: newId('r'),
    name: preset.name,
    sourceId: preset.sourceId,
    enabled: true,
    root,
    actions: { ...DEFAULT_ACTIONS },
    // Market signals change slowly; per-contract rules can repeat sooner.
    throttleSec: preset.sourceId === MARKET ? 120 : 60,
    createdAt: Date.now(),
  };
}
